import React, { useEffect, useState } from 'react';
import { Bell, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { isPushSupported, subscribeToPush } from '../lib/push';

const DISMISS_KEY = 'wd-push-banner-dismissed';

/**
 * Prompts admins to enable push notifications when the app opens
 * (including when launched from the installed PWA). Shows as a
 * sticky banner only when Notification.permission === 'default'
 * and the user has not dismissed it in the current session.
 */
export default function PushPermissionBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    // Permission already granted → silently refresh subscription
    if (Notification.permission === 'granted') {
      subscribeToPush();
      return;
    }
    // Permission denied or session-dismissed → stay hidden
    if (Notification.permission === 'denied') return;
    if (sessionStorage.getItem(DISMISS_KEY) === '1') return;

    // Show banner after a short delay so the UI has time to paint
    const timer = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const res = await subscribeToPush();
      if (res.ok) {
        toast.success('Notificaciones activadas');
        setVisible(false);
      } else if (res.reason === 'denied') {
        toast.error('Permiso denegado. Puedes activarlo desde ajustes del navegador.');
        setVisible(false);
      } else {
        toast.error('No se pudo activar. Intenta de nuevo.');
      }
    } catch (err) {
      toast.error(err?.message || 'Error al activar notificaciones');
    }
    setBusy(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-md z-50 animate-fadeIn"
      data-testid="push-permission-banner"
    >
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-2xl shadow-2xl border border-blue-500/40 overflow-hidden">
        <div className="p-4 sm:p-5 flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <Bell className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm sm:text-base">
              Activar notificaciones
            </div>
            <p className="text-xs sm:text-sm text-blue-100 mt-0.5 leading-relaxed">
              Recibe un aviso al instante cuando llegue una nueva cita, incluso con la app cerrada.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleEnable}
                disabled={busy}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white text-blue-700 text-sm font-semibold hover:bg-blue-50 disabled:opacity-60"
                data-testid="enable-push-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                Activar
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-xs text-blue-100 hover:text-white underline"
                data-testid="dismiss-push-btn"
              >
                Ahora no
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 -mr-1 -mt-1 p-1 text-blue-100 hover:text-white"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
